import { css } from '@emotion/react'
import { memo, useCallback } from 'react'
import type { Message, Club as TClub } from '../../../../../types/mfc'
import { openRoom, pinRoom, streamClubShow } from '../../../../actions/room'
import { openShareThingDrawer } from '../../../../app-state/modals/state.drawer'
import { URL_MFC_SHARE } from '../../../../constants/urls'
import { useUserSelector } from '../../../Hooks/useUser'
import { getThemeSpacing } from '../../../styles/appTheme'

const rootStyles = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
})
const wrapperStyles = css({
    display: 'flex',
    flex: '0 1 auto',
    flexFlow: 'column nowrap',
    textAlign: 'left',
    '& a': {
        textDecoration: 'underline',
        cursor: 'pointer',
    },
})

interface IClub {
    club: TClub
}

const Club = memo((props: IClub) => {
    const {
        club: { name, slug, amt },
    } = props
    const clubShowClick = useCallback(
        (e) => {
            e.stopPropagation()
            e.preventDefault()
            openShareThingDrawer({
                name, // club show name
                thing: slug, // the club show identifier
                thing_type: 'clubs',
            })
        },
        [name, slug],
    )
    return (
        <li>
            <a
                onClick={clubShowClick}
                href={`${URL_MFC_SHARE}/m/${slug}`}
                target="_blank"
                rel="noopener noreferrer"
            >
                {name}
            </a>
            {` - ${amt} token${amt !== 1 ? 's' : ''}`}
        </li>
    )
})

interface IMessageClubShow {
    message: Message
    pinned?: boolean
}

const MessageClubShow = memo((props: IMessageClubShow) => {
    const { message, pinned } = props
    const { from } = message
    const fromID = Number(from)
    const name = useUserSelector(fromID, 'name')
    const slug = useUserSelector(fromID, 'slug')
    const tksid = useUserSelector(fromID, 'clubShow', {}, true, 'tksid')
    const clubs = useUserSelector(fromID, 'clubShow', {}, true, 'clubs')

    const enabledMultipleClubs = clubs.length > 1

    const onClickClubShowMessage = useCallback(() => {
        if (pinned) {
            // 1) open room
            openRoom(fromID)
            // 2) close pin (without closing room)
            pinRoom(fromID, { pinned: false, skipClose: true })
        }

        streamClubShow(fromID)
    }, [fromID, pinned, slug])

    return (
        <div css={rootStyles}>
            <div css={wrapperStyles}>
                <div>{`${name} is in a Club Show, the following Club${
                    enabledMultipleClubs ? 's' : ''
                } ha${enabledMultipleClubs ? 've' : 's'} access:`}</div>
                <ul
                    css={{
                        paddingLeft: getThemeSpacing(4),
                    }}
                >
                    {clubs.map((club, key) => (
                        <Club club={club} key={key} />
                    ))}
                </ul>
                <div>
                    {!tksid ? (
                        `Join ${enabledMultipleClubs ? 'one of ' : ''}the club${
                            enabledMultipleClubs ? 's' : ''
                        } for instant access to the Club Show!`
                    ) : (
                        <span>
                            {`You're in the club, so `}
                            <a
                                onClick={onClickClubShowMessage}
                                data-testid="Link__EnterClubShow"
                            >
                                {`Enter the Club Show`}
                            </a>
                            {`!`}
                        </span>
                    )}
                </div>
            </div>
        </div>
    )
})

export default MessageClubShow
